import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Switch,
  Spinner,
} from '@/components/ui';
import { ApiErrorDisplay } from '@/components/features/error';
import { useService } from '@/hooks/use-services';
import {
  usePaymentLinkMerchant,
  usePaymentLinkServiceConfig,
  useSetPaymentLinkMerchantEnabled,
  useSetPaymentLinkServiceConfig,
  useRemovePaymentLinkServiceConfig,
  useRotatePaymentLinkSalt,
} from '@/hooks/use-payment-link';
import { useAuth } from '@/hooks/use-auth';
import { isMerchantRole } from '@/types/auth';
import { ApiError } from '@/lib/api/client';
import { ActivateMerchantConfirm } from '@/components/features/payment-link/activate-merchant-confirm';
import { RotateSaltConfirm } from '@/components/features/payment-link/rotate-salt-confirm';
import { SaltRevealDialog } from '@/components/features/payment-link/salt-reveal-dialog';
import { WebhookSecretRevealDialog } from '@/components/features/payment-link/webhook-secret-reveal-dialog';
import { ConfirmDialog } from '@/components/features/payment-link/service-config-confirm';
import { ServiceConfigForm } from '@/components/features/payment-link/service-config-form';
import { LinkTester } from '@/components/features/payment-link/link-signer';
import { FORMAT_C1 } from '@/components/features/payment-link/link-signer/format-c1';
import type { PaymentLinkServiceConfigRequest } from '@/types/payment-link';

/** True iff the query failed with exactly this upstream error code (carried in the message). */
function is404(error: unknown, code: string): boolean {
  return error instanceof ApiError && error.status === 404 && error.message === code;
}

/**
 * Payment-Link Service-Wizard (Spec 505 Inc 2b/3). Route `/services/:id/payment-link`, gated on
 * PAYMENT_LINK_WRITE. Loads the merchant provisioning status + the service config and branches
 * into three states (D-Mount / D-NotConfigured / D-Order):
 *   1. merchant not activated  → activate (reuses activate-confirm + salt-reveal) → step 2
 *   2. merchant active, service unconfigured (service_not_configured) → configure the service
 *   3. both configured → status/config view (enable-toggle, DELETE) + link tester
 * Secret reveals are strictly sequential (one reveal state); the cleartext is read from the
 * mutation response, never a refetch (D-Reveal). `noRetry` on the mutations (Phase 4).
 */
export function PaymentLinkServicePage() {
  const { t } = useTranslation('payment-link');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const merchantIdHint = (location.state as { merchantId?: string } | null)?.merchantId;

  const serviceId = id ?? '';
  const serviceQuery = useService(serviceId, merchantIdHint);
  const merchantId = merchantIdHint ?? serviceQuery.data?.merchantId;

  const merchantQuery = usePaymentLinkMerchant(merchantId);
  const serviceConfigQuery = usePaymentLinkServiceConfig(merchantId, serviceId);

  const setMerchantEnabled = useSetPaymentLinkMerchantEnabled();
  const setServiceConfig = useSetPaymentLinkServiceConfig();
  const removeServiceConfig = useRemovePaymentLinkServiceConfig();
  const rotateSalt = useRotatePaymentLinkSalt();

  // Spec 510 D-7/D-9: Merchant-Rollen bestaetigen die Rotation per Tipp-Bestaetigung (ROTATE);
  // Platform-Rollen behalten den einfachen Confirm (wie im Drawer der Betriebsflaeche).
  const { user } = useAuth();
  const requireTypedRotateConfirm = !!user && isMerchantRole(user.role);

  const [activateOpen, setActivateOpen] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [confirm, setConfirm] = useState<'disable' | 'enable' | 'delete' | null>(null);
  // A single reveal state guarantees the two cleartexts are never on screen at once (sequential).
  const [reveal, setReveal] = useState<{ kind: 'salt' | 'webhook'; secret: string } | null>(null);

  const displayName = serviceQuery.data?.displayName || serviceQuery.data?.description || serviceId;

  const backToDetail = () =>
    navigate(`/services/${serviceId}`, { state: merchantId ? { merchantId } : undefined });

  // ---------- Loading / hard-error gates (D-Mount) ----------
  // Wait for the service (source of the merchantId fallback) and, once we have a merchantId, the
  // two provisioning queries. A 404 is NOT an error here — it is a wizard state (handled below).
  const merchantHardError =
    merchantQuery.error && !is404(merchantQuery.error, 'provisioning_record_not_found');
  const serviceConfigHardError =
    serviceConfigQuery.error && !is404(serviceConfigQuery.error, 'service_not_configured');

  if (serviceQuery.isLoading || (merchantId && (merchantQuery.isLoading || serviceConfigQuery.isLoading))) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // Deep-link / lookup failure → error state, never the wizard on half-loaded data (D-Mount).
  if (serviceQuery.error || !serviceQuery.data || !merchantId || merchantHardError || serviceConfigHardError) {
    const displayError: Error =
      (serviceQuery.error as Error | null) ??
      (merchantHardError ? (merchantQuery.error as Error) : null) ??
      (serviceConfigHardError ? (serviceConfigQuery.error as Error) : null) ??
      new Error(t('wizard.loadError'));
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/services')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('wizard.back')}
        </Button>
        <ApiErrorDisplay
          error={displayError}
          onRetry={() => {
            serviceQuery.refetch();
            merchantQuery.refetch();
            serviceConfigQuery.refetch();
          }}
        />
      </div>
    );
  }

  const merchantActivated =
    merchantQuery.data?.Enabled === true && !is404(merchantQuery.error, 'provisioning_record_not_found');
  const serviceConfigured = !!serviceConfigQuery.data && !is404(serviceConfigQuery.error, 'service_not_configured');

  // ---------- Reveal / mutation handlers ----------
  function handleMutationError(err: unknown) {
    setActivateOpen(false);
    setRotateOpen(false);
    setConfirm(null);
    if (err instanceof ApiError && err.status === 503) {
      toast.error(t('retry.unavailable'));
      return;
    }
    toast.error(err instanceof Error ? err.message : t('retry.unavailable'));
  }

  function submitActivate() {
    if (!merchantId) return;
    setMerchantEnabled.mutate(
      { merchantId, enabled: true },
      {
        onSuccess: (res) => {
          setActivateOpen(false);
          if (res.SaltCleartext) setReveal({ kind: 'salt', secret: res.SaltCleartext });
          else toast.success(t('toast.activated'));
          if (res.FacadeCredentialProvisioned === false) toast.error(t('warning.credentialMissing'));
        },
        onError: handleMutationError,
      },
    );
  }

  function submitServiceConfig(body: PaymentLinkServiceConfigRequest) {
    if (!merchantId) return;
    setServiceConfig.mutate(
      { merchantId, serviceId, body },
      {
        onSuccess: (res) => {
          if (res.ClickWebhookSecretCleartext) {
            setReveal({ kind: 'webhook', secret: res.ClickWebhookSecretCleartext });
          } else {
            toast.success(t('serviceConfig.saved'));
          }
        },
        onError: handleMutationError,
      },
    );
  }

  function submitToggle(enabled: boolean) {
    if (!merchantId) return;
    setServiceConfig.mutate(
      { merchantId, serviceId, body: { Enabled: enabled } },
      {
        onSuccess: () => {
          setConfirm(null);
          toast.success(t('serviceConfig.saved'));
        },
        onError: handleMutationError,
      },
    );
  }

  // Spec 510: Rotation aus dem Konfiguriert-Zustand. Das Reveal speist sich aus der
  // Mutation-Response (Secret-once), nie aus einem Refetch; der EINE reveal-State haelt
  // die Reveals sequentiell. KEINE retry-Option (Spec Trap 4 — useMutation retryt nicht).
  function submitRotate() {
    if (!merchantId) return;
    rotateSalt.mutate(
      { merchantId },
      {
        onSuccess: (res) => {
          setRotateOpen(false);
          setReveal({ kind: 'salt', secret: res.SaltCleartext });
        },
        onError: handleMutationError,
      },
    );
  }

  function submitDelete() {
    if (!merchantId) return;
    removeServiceConfig.mutate(
      { merchantId, serviceId },
      {
        onSuccess: () => {
          setConfirm(null);
          toast.success(t('deleteConfirm.toast'));
        },
        onError: handleMutationError,
      },
    );
  }

  const header = (
    <div>
      <Button variant="outline" size="sm" onClick={backToDetail}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        {t('wizard.back')}
      </Button>
      <h1 className="mt-3 text-xl font-semibold">{t('wizard.title')}</h1>
      <p className="text-sm text-muted-foreground">{displayName}</p>
    </div>
  );

  // ---------- State 1: merchant not activated ----------
  const step1 = !merchantActivated && (
    <Card>
      <CardHeader>
        <CardTitle>{t('wizard.step1Title')}</CardTitle>
        <CardDescription>{t('wizard.activateIntro')}</CardDescription>
      </CardHeader>
      <CardContent>
        <Button onClick={() => setActivateOpen(true)} disabled={setMerchantEnabled.isPending}>
          {t('wizard.activateButton')}
        </Button>
      </CardContent>
    </Card>
  );

  // ---------- State 2: merchant active, service unconfigured ----------
  const step2 = merchantActivated && !serviceConfigured && (
    <Card>
      <CardHeader>
        <CardTitle>{t('wizard.step2Title')}</CardTitle>
        <CardDescription className="flex items-center gap-2 text-success">
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          {t('wizard.merchantAlreadyActive')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ServiceConfigForm
          mode="create"
          onSubmit={submitServiceConfig}
          isPending={setServiceConfig.isPending}
        />
      </CardContent>
    </Card>
  );

  // ---------- State 3: configured ----------
  const config = serviceConfigQuery.data;
  const step3 = merchantActivated && serviceConfigured && config && (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('serviceConfig.sectionTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="pl-enabled">{t('serviceConfig.enabledLabel')}</Label>
            <Switch
              id="pl-enabled"
              checked={config.Enabled}
              onCheckedChange={() => setConfirm(config.Enabled ? 'disable' : 'enable')}
              disabled={setServiceConfig.isPending}
            />
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t('serviceConfig.idempotencyLabel')}</span>
            <span>{t(`serviceConfig.idempotency.${config.IdempotencyMode}`)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t('serviceConfig.clickWebhookLabel')}</span>
            <span>
              {config.ClickNotification.Enabled && config.ClickNotification.Url
                ? config.ClickNotification.Url
                : t('common.no')}
            </span>
          </div>
          {config.ClickNotification.HasSecret && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />
              {t('serviceConfig.hasSecretLabel')}
            </div>
          )}
          {/* Merchant-level rotation counts (context for this service's provisioning). */}
          {merchantQuery.data && (
            <div className="space-y-1 border-t pt-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('drawer.fields.saltRotationCount')}</span>
                <span className="tabular-nums">{merchantQuery.data.SaltRotationCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">
                  {t('drawer.fields.credentialRotationCount')}
                </span>
                <span className="tabular-nums">{merchantQuery.data.CredentialRotationCount}</span>
              </div>
              {/* Spec 510: merchant-erreichbare Rotations-Affordanz (die "bei Verlust
                  rotieren"-Copy braucht einen bedienbaren Weg; Platform rotiert auch hier
                  oder weiter im Drawer der Betriebsflaeche). */}
              <div className="pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRotateOpen(true)}
                  disabled={rotateSalt.isPending}
                >
                  {t('actions.rotateSalt')}
                </Button>
              </div>
            </div>
          )}
          <div className="pt-2">
            <Button
              variant="outline"
              className="text-destructive"
              onClick={() => setConfirm('delete')}
              disabled={removeServiceConfig.isPending}
            >
              {t('deleteConfirm.title')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('wizard.integrationTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
            {t('wizard.integrationSaltNote')}
          </p>
          <p>{t('wizard.integrationHmacNote')}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('wizard.testerTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Spec 516 D-8: der bereits geladene Service wird durchgereicht —
              der eingebettete Tester macht damit KEINEN eigenen Detail-Fetch
              und baut die Link-Base auf publicBaseUrl (falls gesetzt). */}
          <LinkTester
            format={FORMAT_C1}
            initialServiceId={serviceId}
            presetService={
              serviceQuery.data
                ? {
                    serviceId: serviceQuery.data.serviceId,
                    merchantId: serviceQuery.data.merchantId,
                    publicBaseUrl: serviceQuery.data.publicBaseUrl,
                  }
                : undefined
            }
          />
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {header}
      {step1}
      {step2}
      {step3}

      <ActivateMerchantConfirm
        open={activateOpen}
        onOpenChange={(open) => !open && setActivateOpen(false)}
        merchantName={merchantId}
        enabled
        onConfirm={submitActivate}
        isPending={setMerchantEnabled.isPending}
      />

      <ConfirmDialog
        open={confirm === 'disable' || confirm === 'enable'}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={confirm === 'disable' ? t('toggleConfirm.disableTitle') : t('toggleConfirm.enableTitle')}
        body={confirm === 'disable' ? t('toggleConfirm.disableBody') : t('toggleConfirm.enableBody')}
        confirmLabel={confirm === 'disable' ? t('toggleConfirm.disableSubmit') : t('toggleConfirm.enableSubmit')}
        cancelLabel={t('toggleConfirm.cancel')}
        destructive={confirm === 'disable'}
        onConfirm={() => submitToggle(confirm === 'enable')}
        isPending={setServiceConfig.isPending}
      />

      <RotateSaltConfirm
        open={rotateOpen}
        onOpenChange={(open) => !open && setRotateOpen(false)}
        merchantName={merchantId}
        onConfirm={submitRotate}
        isPending={rotateSalt.isPending}
        requireTypedConfirmation={requireTypedRotateConfirm}
      />

      <ConfirmDialog
        open={confirm === 'delete'}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={t('deleteConfirm.title')}
        body={t('deleteConfirm.body')}
        confirmLabel={t('deleteConfirm.submit')}
        cancelLabel={t('deleteConfirm.cancel')}
        destructive
        onConfirm={submitDelete}
        isPending={removeServiceConfig.isPending}
      />

      {reveal?.kind === 'salt' && (
        <SaltRevealDialog
          open
          onOpenChange={(open) => !open && setReveal(null)}
          saltCleartext={reveal.secret}
          onConfirm={() => setReveal(null)}
        />
      )}
      {reveal?.kind === 'webhook' && (
        <WebhookSecretRevealDialog
          open
          onOpenChange={(open) => !open && setReveal(null)}
          secretCleartext={reveal.secret}
          onConfirm={() => setReveal(null)}
        />
      )}
    </div>
  );
}
